import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { QueryParams } from './api';
import { useAuth } from './auth';
import type { HelpdeskService, TenantRole } from './types';

/**
 * The help desk a page works on. Work orders, departments, setup, and reports serve both the technology help desk and
 * facilities; pages under /facilities are wrapped in a facilities DeskProvider, and everything else is the technology
 * help desk.
 */
export interface Desk {
  service: HelpdeskService;
  facilities: boolean;
  /** Where this help desk's pages start: '' for the technology help desk, '/facilities' for facilities. */
  base: string;
  /** "the technology team" or "the facilities team". */
  team: string;
  /** "department" or "category". */
  departmentWord: string;
  /** "Departments" or "Categories". */
  departmentsLabel: string;
  /** The signed-in user's role on this help desk. */
  role: TenantRole | null;
  can(role: TenantRole): boolean;
  /** Query parameters with this help desk added. */
  query(params?: QueryParams): QueryParams;
  /** An API path with ?service= added, for requests that don't take query parameters. */
  path(path: string): string;
  workOrderHref(id: string): string;
}

const DeskContext = createContext<HelpdeskService>('IT');

export function DeskProvider({ service, children }: { service: HelpdeskService; children: ReactNode }) {
  return <DeskContext.Provider value={service}>{children}</DeskContext.Provider>;
}

export const deskBase = (service: HelpdeskService) => (service === 'FACILITIES' ? '/facilities' : '');

/** A ticket's page on its own help desk. */
export const workOrderHref = (service: HelpdeskService, id: string) => `${deskBase(service)}/work-orders/${id}`;

export function useDeskFor(service: HelpdeskService): Desk {
  const { me, can, canFacilities } = useAuth();
  return useMemo(() => {
    const facilities = service === 'FACILITIES';
    return {
      service,
      facilities,
      base: deskBase(service),
      team: facilities ? 'the facilities team' : 'the technology team',
      departmentWord: facilities ? 'category' : 'department',
      departmentsLabel: facilities ? 'Categories' : 'Departments',
      role: facilities ? (me?.facilitiesRole ?? null) : (me?.role ?? null),
      can: facilities ? canFacilities : can,
      query: (params: QueryParams = {}) => ({ ...params, service }),
      path: (path: string) => `${path}${path.includes('?') ? '&' : '?'}service=${service}`,
      workOrderHref: (id: string) => workOrderHref(service, id),
    };
  }, [service, me, can, canFacilities]);
}

/** The help desk the current page is for. */
export function useDesk() {
  return useDeskFor(useContext(DeskContext));
}
